import { TopicType } from '@/utils/constants'
import { ConnectionModel } from './connection'
import { Note, Topic } from './topic'

// export enum ActivityType {
//   LIKE = 1,
//   COMMENT = 2,
//   FOLLOW = 3,
// }

export enum SocketEventType {
  USER_LOGGED_IN = 'User_login',
  USER_BECAME_ONLINE = 'User_online',
  USER_GONE_OFFLINE = 'User_offline',
  CONNECTION_REQUESTED = 'connection_requested',
  USER_CONNECTED = 'connected',
  TOPIC_ADDED = 'topic_added',
  TOPIC_SHARED = 'topic_shared',
  TOPIC_UPDATED = 'topic_updated',
  TOPIC_DELETED = 'topic_deleted',
  TOPIC_VIEWED = 'topic_view',
  TOPIC_NOTE_LIKED = 'Note_Thumbs_Up',
  TOPIC_NOTE_UNLIKED = 'Note_Thumbs_Down',
  TOPIC_NOTE_ADDED = 'topic_note_added',
  TOPIC_NOTE_UPDATED = 'topic_note_updated',
  TOPIC_NOTE_DELETED = 'topic_note_deleted',
  NOTE_COMMENT_ADDED = 'Note_comments',
  NOTE_COMMENT_DELETED = 'Note_comments_deleted',
  TOPIC_PHOTO_ADDED = 'topic_photo_added',
  TOPIC_PHOTO_DELETED = 'topic_photo_deleted',
  PHOTO_COMMENT_ADDED = 'comments',
  PHOTO_COMMENT_DELETED = 'photo_comments_deleted',
  PHOTO_LIKED = 'like',
  PHOTO_UNLIKED = 'dislike',
}

/** Visual/filter grouping for a notification — drives the icon badge + the page's filter chips. */
export type NotificationKind = 'like' | 'comment' | 'follow' | 'request' | 'upload' | 'share'

export class ActivityTypeUtil {
  static getActivityText(eventType: SocketEventType): string {
    let result = ''
    switch (eventType) {
      case SocketEventType.TOPIC_NOTE_LIKED:
      case SocketEventType.PHOTO_LIKED:
        result = 'liked'
        break
      case SocketEventType.NOTE_COMMENT_ADDED:
      case SocketEventType.PHOTO_COMMENT_ADDED:
        result = 'commented on'
        break
      case SocketEventType.USER_CONNECTED:
        result = 'followed you'
        break
      case SocketEventType.CONNECTION_REQUESTED:
        result = 'wants to connect with you'
        break
      case SocketEventType.TOPIC_SHARED:
        result = 'shared'
        break
      case SocketEventType.TOPIC_PHOTO_ADDED:
        result = 'added photos to'
        break
    }
    return result
  }

  /** Notification-worthy event types — presence/view events (login, online, viewed…) are excluded on purpose. */
  static isSocketEventSupported(eventType: SocketEventType): boolean {
    return [
      SocketEventType.TOPIC_NOTE_LIKED,
      SocketEventType.PHOTO_LIKED,
      SocketEventType.NOTE_COMMENT_ADDED,
      SocketEventType.PHOTO_COMMENT_ADDED,
      SocketEventType.USER_CONNECTED,
      SocketEventType.CONNECTION_REQUESTED,
      SocketEventType.TOPIC_SHARED,
      SocketEventType.TOPIC_PHOTO_ADDED,
    ].includes(eventType)
  }

  static isTopicRelatedEvent(eventType: SocketEventType): boolean {
    return [
      SocketEventType.TOPIC_NOTE_LIKED,
      SocketEventType.PHOTO_LIKED,
      SocketEventType.NOTE_COMMENT_ADDED,
      SocketEventType.PHOTO_COMMENT_ADDED,
      SocketEventType.TOPIC_SHARED,
      SocketEventType.TOPIC_PHOTO_ADDED,
    ].includes(eventType)
  }

  static getKind(eventType: SocketEventType): NotificationKind {
    switch (eventType) {
      case SocketEventType.TOPIC_NOTE_LIKED:
      case SocketEventType.PHOTO_LIKED:
        return 'like'
      case SocketEventType.NOTE_COMMENT_ADDED:
      case SocketEventType.PHOTO_COMMENT_ADDED:
        return 'comment'
      case SocketEventType.USER_CONNECTED:
        return 'follow'
      case SocketEventType.CONNECTION_REQUESTED:
        return 'request'
      case SocketEventType.TOPIC_PHOTO_ADDED:
        return 'upload'
      case SocketEventType.TOPIC_SHARED:
        return 'share'
      default:
        return 'comment'
    }
  }

  static hasCommentText(eventType: SocketEventType): boolean {
    return (
      eventType == SocketEventType.NOTE_COMMENT_ADDED || eventType == SocketEventType.PHOTO_COMMENT_ADDED
    )
  }

  static getTopicID(item: NotificationItem): string {
    return item.secondID
  }

  static getNoteID(item: NotificationItem): string {
    return item.objectID
  }

  static getTopicTitle(item: NotificationItem): string {
    return item.secondName
  }

  static getCommentText(item: NotificationItem): string {
    return ActivityTypeUtil.hasCommentText(item.eventType) ? item.thirdName : ''
  }
}

export interface SocketMessage {
  userName: string
  eventData: EventData
}

export interface EventData {
  eventID: string
  eventType: SocketEventType
  eventDateTime: string
  userID: string
  userName: string
  userFullName: string
  sourceUserID: string
  sourceUserName: string
  sourceUserFullName: string
  profilePicURL: string
  profilePicThumbURL: string
  profilePicFullURL: string
  objectID: string
  objectName: string
  secondID: string
  secondName: string
  thirdID: string
  thirdName: string
  variableData: string
}

export interface NotificationItem extends EventData {
  isRead: boolean
  topicApplication: number
  topicType: TopicType
  visibility: number
  extraData: Record<string, any>[]
}

/**
 * A single "someone viewed this topic" sighting, derived from a live
 * `topic_view` socket event (see useWebSocketHook.ts). Only ever delivered to
 * the topic owner — the backend never notifies anyone else about a view.
 */
export interface TopicViewerEntry {
  sourceUserID: string
  sourceUserFullName: string
  profilePicThumbURL: string
  /** Server-reported `eventDateTime` (ms epoch) — used for staleness, not
   *  client receipt time, since a fresh socket connection replays any events
   *  that were queued while it was offline. */
  viewedAt: number
}
